import type { ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ISSUE_LABEL, RANGE_LABEL, type IssueReason, type ResourceState, type WorkloadSummary } from "@k8s-dashboard/contracts";
import { drillKeys, useNamespaceDetail, useResourceCatalog, useScope } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { LoadingState, SectionView } from "@/components/SectionState";
import { HttpError } from "@/api/client";
import { stateFromError, stateNotice } from "@/features/resources/state";
import { VirtualTable } from "@/components/VirtualTable";
import { Breadcrumb, IssueFilter, TrendsGrid, UsageBar, drillCrumbs, refPath } from "@/components/drill";
import { EventFeed } from "@/features/overview/panels";
import { duration, num } from "@/lib/format";
import { PageError, PageHeader, useDrillControls, useInvalidate, useIssueFilter } from "./common";

/**
 * Namespace 상세 — 이슈 #15
 * Deployment/StatefulSet/DaemonSet 상태 표, 사용량 비율, 상태 필터, 추세, 이벤트.
 * Workload가 수백 개여도 견디도록 목록은 가상 스크롤입니다.
 */
export function NamespaceDetail() {
  const { search } = useLocation();
  const { namespace = "" } = useParams();
  const { clusterId, range, from, to, refreshMs } = useDashboardParams();
  const q = useNamespaceDetail(clusterId, namespace, range, refreshMs, from, to);
  const invalidate = useInvalidate(drillKeys.namespace(clusterId, namespace, range));
  /* 이 화면의 namespace는 경로가 정합니다. Header의 전역 데이터 선택과 독립이며,
     상세 경로를 전역 선택으로 채택하지 않습니다. */
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);
  const filter = useIssueFilter();

  const summary = q.data?.summary.data;
  const workloads = q.data?.workloads.data ?? [];
  const available = [...new Set(workloads.flatMap((w) => w.issues))] as IssueReason[];
  const counts = Object.fromEntries(
    available.map((r) => [r, workloads.filter((w) => w.issues.includes(r)).length]),
  ) as Partial<Record<IssueReason, number>>;
  const shown = filter.selected.length
    ? workloads.filter((w) => filter.selected.every((r) => w.issues.includes(r)))
    : workloads;

  return (
    <div className="page">
      <PageHeader
        crumbs={
          <Breadcrumb
            items={drillCrumbs(search, undefined, { label: namespace })}
          />
        }
        title={namespace}
        subtitle={`${clusterId} · ${RANGE_LABEL[range]}`}
        controls={controls}
        actions={
          <IssueFilter
            available={available}
            selected={filter.selected}
            counts={counts}
            onToggle={filter.toggle}
            onClear={filter.clear}
          />
        }
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          {summary && (
            <div className="grid grid--kpi">
              <StatTile
                label="Workload"
                value={`${num(summary.workloads.total - summary.workloads.unhealthy)}/${num(summary.workloads.total)}`}
                tone={summary.workloads.unhealthy > 0 ? "warning" : undefined}
                footnote={`이상 ${summary.workloads.unhealthy}개`}
              />
              <StatTile
                label="Pod"
                value={`${num(summary.pods.running)}/${num(summary.pods.total)}`}
                tone={summary.pods.failed > 0 ? "critical" : undefined}
                footnote={`Pending ${summary.pods.pending} · Failed ${summary.pods.failed}`}
              />
              <StatTile
                label="재시작"
                value={num(summary.pods.restarts)}
                delta={summary.pods.restarts > 0 ? { text: "↑", kind: "bad" } : { text: "→", kind: "flat" }}
                footnote={`${RANGE_LABEL[range]} 합계`}
              />
              <StatTile
                label="CPU / Request"
                value={`${(summary.usage.cpuVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(summary.usage.cpuMilli)}m / ${num(summary.usage.cpuRequestMilli)}m`}
              />
              <StatTile
                label="Memory / Request"
                value={`${(summary.usage.memoryVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(summary.usage.memoryMib)}MiB / ${num(summary.usage.memoryRequestMib)}MiB`}
              />
            </div>
          )}

          <NamespaceExplorerLinks clusterId={clusterId} />

          <Panel
            title="Workload"
            subtitle={`${num(shown.length)}/${num(workloads.length)}개 표시 · 이름을 클릭하면 상세로 이동합니다`}
            section={q.data?.workloads}
            referenceIso={q.data?.generatedAt}
            flush
          >
            <SectionView
              section={q.data?.workloads}
              loading={q.isLoading}
              emptyTitle="Workload가 없습니다"
              emptyDetail="이 Namespace에 배포된 Workload가 없습니다."
            >
              {() => <WorkloadTable items={shown} search={search} />}
            </SectionView>
          </Panel>

          <SectionView
            section={q.data?.trends}
            loading={q.isLoading}
            emptyTitle="추세 데이터가 없습니다"
          >
            {(panels) => (
              <TrendsGrid panels={panels} section={q.data?.trends} range={range} referenceIso={q.data?.generatedAt} labelPrefix={namespace} />
            )}
          </SectionView>

          <Panel title="최근 Event" section={q.data?.events} referenceIso={q.data?.generatedAt} flush>
            <SectionView section={q.data?.events} loading={q.isLoading} emptyTitle="최근 이벤트가 없습니다">
              {(events) => <EventFeed events={events} referenceIso={q.data!.generatedAt} />}
            </SectionView>
          </Panel>
        </>
      )}
    </div>
  );
}

/**
 * Namespace의 Quota · LimitRange 바로가기 (#206 · #203 §1)
 * --------------------------------------------------------------------------
 * **카드마다·리소스마다 GET·LIST를 띄우지 않습니다** (ADR 0002). 읽는 것은 Explorer
 * 화면과 **같은 캐시 키**를 쓰는 카탈로그 질의 하나뿐이고, 거기서 "이 GVR이 지금 조회
 * 가능한가"만 봅니다. 요청 수는 캐시 상태가 정합니다 — 캐시가 비어 있으면 카탈로그
 * GET **한 번**이 늘고, 신선하면(`staleTime` 60초) 0이며, stale이면 재검증 한 번이
 * 나갈 수 있습니다. `useScope()`는 이미 앱 전역이 쓰는 질의라 이 카드가 더하지 않습니다.
 * 리소스 목록·상세 요청은 어느 경우에도 0입니다.
 *
 * 링크 목적지는 기존 `/resources`라 Scope 강제·목록 상한·cursor가 전부 그대로입니다.
 * **`ns`·`cluster` 질의는 만들지 않고 전역 선택도 바꾸지 않습니다.** 상세 경로의
 * namespace는 이 객체의 신원이며 전역 데이터 선택의 권한이나 초안이 아닙니다.
 * Explorer에서는 사용자가 현재 전역 선택을 명시적으로 적용합니다.
 *
 * 켜지지 않은 배포에서 링크를 **조용히 숨기지 않습니다.** "기능 없음 · 권한 없음 ·
 * 권한 확인 실패 · 카탈로그 오류 · 미등재 · 비(非)ready"는 서로 다른 문장을 받습니다.
 * 사유로는 우리가 가진 고정 문구만 쓰고 서버 응답 본문을 그대로 옮기지 않습니다.
 */
const NAMESPACE_EXPLORER_LINKS = [
  { group: "core", version: "v1", resource: "resourcequotas", label: "ResourceQuota" },
  { group: "core", version: "v1", resource: "limitranges", label: "LimitRange" },
] as const;

/** 등재는 됐지만 지금 열 수 없는 이유. 서버 사유를 옮기지 않고 상태별 고정 문구를 씁니다. */
const DESCRIPTOR_NOTE: Record<Exclude<ResourceState, "ready">, string> = {
  syncing: "리소스 캐시를 동기화하는 중입니다. 잠시 후 다시 보세요.",
  unsupported: "이 API는 metadata 전용 조회를 지원하지 않습니다.",
  forbidden: "대시보드 ServiceAccount에 이 리소스의 조회 권한이 없습니다.",
  missing: "클러스터가 이 API를 제공하지 않습니다.",
};

const UNLISTED_NOTE = "이 배포의 Explorer 대상 목록에 없습니다. 배포 설정에서 열 수 있습니다.";

/**
 * 인가 실패인가. 401·403은 **갱신 실패가 아니라 상태 변화**입니다 — 권한이 회수된 뒤
 * 손에 남은 캐시로 링크를 계속 열어 두면 사용자는 403을 받고서야 알게 됩니다. 그래서
 * 캐시 유무와 무관하게 닫습니다.
 */
function isAuthDenied(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
}

type LinkNote = {
  /** 테스트·진단이 읽는 안정 키. 화면에는 나오지 않습니다. */
  state: string;
  title: string;
  detail: string;
  tone: "neutral" | "forbidden" | "degraded" | "error";
};

/** 상태 한 줄. 색 단독으로 전하지 않고 글리프 + 문구가 항상 함께 갑니다. */
function NoteBlock({ note }: { note: LinkNote }) {
  return (
    <div
      className={note.tone === "neutral" ? "state" : `state state--${note.tone}`}
      role={note.tone === "forbidden" || note.tone === "error" ? "alert" : undefined}
      data-state={note.state}
    >
      <span className="state__glyph" aria-hidden="true">
        {note.tone === "neutral" ? "✓" : note.tone === "forbidden" ? "!" : "▲"}
      </span>
      <span className="state__title">{note.title}</span>
      <span className="state__detail">{note.detail}</span>
    </div>
  );
}

function NamespaceExplorerLinks({ clusterId }: { clusterId: string }) {
  const scope = useScope();
  const canExplore = scope.data?.canExploreResources ?? false;
  const catalog = useResourceCatalog(clusterId, canExplore);

  /**
   * **오류가 나면 캐시가 있어도 링크를 닫습니다.**
   *
   * 401·403은 명백합니다 — 권한이 회수된 뒤에도 열려 있는 바로가기는 "준비됨"이라고
   * 거짓말을 합니다. 5xx도 같게 다룹니다: 낡은 목록에 stale 배지를 붙여 열어 두는 길도
   * 있지만, 그러려면 신선도 표시와 그 표시를 읽는 규칙이 새로 생깁니다. **막는 쪽이 더
   * 작고 안전한 동작**이고, 잃는 것은 상류가 흔들리는 동안의 바로가기 두 개뿐입니다.
   * 대신 사유는 구분해서 말합니다 — 권한 없음과 일시적 장애는 다른 행동을 요구합니다.
   */
  let body: ReactNode;
  if (scope.isLoading || (canExplore && catalog.isLoading)) {
    body = <LoadingState lines={1} height={64} />;
  } else if (isAuthDenied(scope.error)) {
    body = (
      <NoteBlock
        note={{
          state: "scope-forbidden",
          title: "리소스 탐색 권한이 없습니다",
          detail: "이 계정의 권한으로는 열 수 없습니다. 권한이 방금 바뀌었다면 다시 로그인하세요.",
          tone: "forbidden",
        }}
      />
    );
  } else if (scope.isError) {
    body = (
      <NoteBlock
        note={{
          state: "scope-unknown",
          title: "리소스 탐색을 쓸 수 있는지 확인하지 못했습니다",
          detail: "권한 정보를 읽지 못해 바로가기를 열지 않습니다. 화면을 새로 고치면 다시 확인합니다.",
          tone: "degraded",
        }}
      />
    );
  } else if (!canExplore) {
    body = <NoteBlock note={stateNotice("unavailable")} />;
  } else if (isAuthDenied(catalog.error)) {
    body = (
      <NoteBlock
        note={{
          state: "catalog-forbidden",
          title: "리소스 목록을 볼 권한이 없습니다",
          detail: "권한이 회수되었거나 범위 밖입니다. 앞서 받아 둔 목록은 더 쓰지 않습니다.",
          tone: "forbidden",
        }}
      />
    );
  } else if (catalog.error) {
    const mapped = stateFromError(catalog.error);
    body = mapped ? (
      <NoteBlock note={stateNotice(mapped)} />
    ) : (
      <NoteBlock
        note={{
          state: "catalog-error",
          title: "리소스 카탈로그를 불러오지 못했습니다",
          /* 서버 응답 본문을 옮기지 않습니다. 사용자가 할 일은 어느 코드에서나 같습니다. */
          detail: "지금은 확인할 수 없어 바로가기를 열지 않습니다. 잠시 후 다시 시도하세요.",
          tone: "error",
        }}
      />
    );
  } else {
    const descriptors = catalog.data?.items ?? [];
    body = (
      <dl className="chips" aria-label="Quota · LimitRange 바로가기">
        {NAMESPACE_EXPLORER_LINKS.map((entry) => {
          const key = `${entry.group}/${entry.version}/${entry.resource}`;
          const descriptor = descriptors.find(
            (d) => d.group === entry.group && d.version === entry.version && d.resource === entry.resource,
          );
          /* null이면 링크를 만들 수 있는 상태입니다. 그 외에는 이유를 문장으로 말합니다. */
          const blocked =
            descriptor === undefined ? UNLISTED_NOTE : descriptor.state === "ready" ? null : DESCRIPTOR_NOTE[descriptor.state];
          return (
            <div key={key}>
              <dt className="field__label">{entry.label}</dt>
              <dd data-testid={`ns-explorer-${entry.resource}`} data-state={descriptor?.state ?? "unlisted"}>
                {blocked === null ? (
                  <Link
                    to={{ pathname: "/resources", search: `?${new URLSearchParams({ res: key }).toString()}` }}
                  >
                    {entry.label} 보기
                  </Link>
                ) : (
                  <span className="muted">{blocked}</span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  }

  return (
    <Panel title="Quota · LimitRange" subtitle="Resource Explorer에서 이 Namespace로 좁혀 봅니다">
      {body}
    </Panel>
  );
}

const COLS = ["30%", "9%", "10%", "10%", "8%", "15%", "18%"];

function WorkloadTable({ items, search }: { items: WorkloadSummary[]; search: string }) {
  if (items.length === 0) {
    return (
      <div className="state">
        <span className="state__glyph" aria-hidden="true">
          ✓
        </span>
        <span className="state__title">필터에 맞는 Workload가 없습니다</span>
      </div>
    );
  }
  return (
    <VirtualTable
      items={items}
      rowHeight={34}
      height={440}
      getKey={(w) => w.ref.workloadUid ?? w.name}
      header={
        <tr>
          <th style={{ width: COLS[0] }}>이름</th>
          <th style={{ width: COLS[1] }}>Kind</th>
          <th style={{ width: COLS[2] }}>상태</th>
          <th className="ds-num" style={{ width: COLS[3] }}>
            Replica
          </th>
          <th className="ds-num" style={{ width: COLS[4] }}>
            재시작
          </th>
          <th style={{ width: COLS[5] }}>CPU / Request</th>
          <th style={{ width: COLS[6] }}>이미지</th>
        </tr>
      }
      renderRow={(w) => (
        <>
          <td className="ds-ident" style={{ width: COLS[0] }}>
            <Link to={refPath({ workloadKind: w.kind, workloadName: w.name, namespace: w.namespace }, search)}>
              {w.name}
            </Link>
          </td>
          <td style={{ width: COLS[1] }}>{w.kind}</td>
          <td style={{ width: COLS[2] }}>
            <StatusBadge
              severity={w.severity}
              label={w.issues.length ? ISSUE_LABEL[w.issues[0]!] : "Healthy"}
              small
            />
          </td>
          <td className="ds-num" style={{ width: COLS[3] }}>
            {w.replicas.ready}/{w.replicas.desired}
          </td>
          <td className="ds-num" style={{ width: COLS[4] }}>
            {w.restarts}
          </td>
          <td style={{ width: COLS[5] }}>
            <UsageBar ratio={w.usage.cpuVsRequest} label="CPU Request" />
          </td>
          <td className="ds-ident" style={{ width: COLS[6] }} title={`${w.images[0]} · age ${duration(w.ageSeconds)}`}>
            {w.images[0]}
          </td>
        </>
      )}
    />
  );
}
